import {
  ButtonV2 as Button,
  Flex,
  TextInputV2 as TextInput,
  TagsInput,
  DropdownV3,
} from 'portal-commons';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';

import FormSection from './FormSection';
import FormTitle from './FormTitle';
import { DefaultSenderIdConfigurationFormValues } from './constants';
import { useSendIdConfigurationFormValues } from './hooks';
import { SenderIdConfigurationFormValues } from './types';
import {
  createRequiredValidator,
  isValidInput,
  isValidSenderIds,
} from '../../validators';
import { DcaOptions as dcaOptions } from '../../constants';

interface SenderIdConfigurationFormProps {
  proceeding?: boolean;
  defaultValues?: SenderIdConfigurationFormValues;
  onChange?: (data: SenderIdConfigurationFormValues) => void;
  onBack?: () => void;
  onSubmit?: () => void;
}

const SenderIdConfigurationForm: FunctionComponent<
  SenderIdConfigurationFormProps
> = ({ proceeding, defaultValues, onChange, onBack, onSubmit }) => {
  const {
    control,
    formState: { errors, isValidating },
    reset,
    handleSubmit,
    watch,
  } = useForm<SenderIdConfigurationFormValues>({
    mode: 'onTouched',
    defaultValues,
  });
  const { senderIds, addSenderId } = useSendIdConfigurationFormValues(control);
  const [isSkipping, setIsSkipping] = useState(false);

  const handleValid = () => {
    onSubmit?.();
  };

  const handleSkip = () => {
    reset(DefaultSenderIdConfigurationFormValues);
    setIsSkipping(true);
  };

  useEffect(() => {
    if (isSkipping) {
      onSubmit?.();
      setIsSkipping(false);
    }
  }, [isSkipping, onSubmit]);

  useEffect(() => {
    const { unsubscribe } = watch((data) => {
      onChange?.(data as SenderIdConfigurationFormValues);
    });
    return () => unsubscribe();
  }, [watch, onChange]);

  return (
    <form noValidate onSubmit={handleSubmit(handleValid)}>
      <Flex sx={{ flexDirection: 'column', gap: 'xl', width: '100%' }}>
        <FormTitle
          title="Sender ID Configuration (Optional)"
          description="Define the specific Sender IDs allowed and blocked for this brand. You can configure this later."
        />
        {senderIds.map((senderId, index) => (
          <FormSection key={senderId.id}>
            <Controller
              control={control}
              name={`senderIds.${index}.senderId`}
              rules={{
                validate: {
                  isRequired: createRequiredValidator('Please enter Sender ID'),
                  isValidInput,
                  isValidPattern: (value) =>
                    /^[a-zA-Z0-9 ]{3,11}$/.test(value) ||
                    'Sender ID must be 3-11 characters (alphanumeric and spaces only)',
                },
              }}
              render={({
                field: { value, onChange: fieldOnChange, onBlur },
                fieldState: { error },
              }) => (
                <TextInput
                  error={!!error?.message}
                  label="Sender ID to Allow"
                  note={error?.message}
                  value={value}
                  onChange={fieldOnChange}
                  onBlur={onBlur}
                  data-testid="senderId"
                />
              )}
            />
            <Controller
              control={control}
              name={`senderIds.${index}.associatedDcaList`}
              rules={{
                validate: {
                  isRequired: createRequiredValidator(
                    'Please select Associated DCA'
                  ),
                },
              }}
              render={({
                field: { value, onChange: fieldOnChange, onBlur },
                fieldState: { error },
              }) => (
                <DropdownV3
                  required
                  error={!!error?.message}
                  label="Associated DCA"
                  note={error?.message}
                  options={dcaOptions}
                  value={value}
                  onBlur={onBlur}
                  onChange={fieldOnChange}
                  data-testid="associatedDcaList"
                />
              )}
            />
            <Controller
              control={control}
              name={`senderIds.${index}.protectedSenderIdList`}
              rules={{
                validate: { isValidSenderIds },
              }}
              render={({
                field: { value, onChange: fieldOnChange },
                fieldState: { error },
              }) => {
                const tags = useMemo(
                  () =>
                    value
                      ? value
                          .split(',')
                          .filter((tag: string) => tag.trim() !== '')
                      : [],
                  [value]
                );
                return (
                  <TagsInput
                    sx={{ gridColumn: 'span 2' }}
                    slotProps={{ contentWrapper: { borderWidth: '1.5px' } }}
                    error={!!error?.message}
                    label="Sender IDs to Block (Max 5)"
                    helperText={error?.message}
                    tagRegex={/^[0-9a-zA-Z ]+$/}
                    tags={tags}
                    onChange={(updatedTags: string[]) =>
                      fieldOnChange(updatedTags.join(','))
                    }
                    data-testid="protectedSenderIdList"
                  />
                );
              }}
            />
            {index === senderIds.length - 1 && senderIds.length < 5 && (
              <Flex sx={{ gridColumn: 'span 2', marginTop: '-16px' }}>
                <Button
                  size="lg"
                  shape="square"
                  variant="transparent"
                  startAdornment={faPlus}
                  onClick={() => {
                    addSenderId({
                      senderId: '',
                      associatedDcaList: '',
                      protectedSenderIdList: '',
                    });
                  }}
                  data-testid="addSenderId"
                >
                  Add Sender ID
                </Button>
              </Flex>
            )}
          </FormSection>
        ))}
        <Flex sx={{ justifyContent: 'flex-end', gap: 'm' }}>
          <Button
            shape="square"
            variant="outline"
            size="lg"
            color="secondary"
            disabled={proceeding}
            onClick={onBack}
            data-testid="back"
          >
            Back
          </Button>
          <div style={{ flex: 1 }} />
          <Button
            shape="square"
            variant="outline"
            size="lg"
            color="secondary"
            disabled={proceeding}
            onClick={handleSkip}
            data-testid="skip"
          >
            Skip & Submit
          </Button>
          <Button
            disabled={
              proceeding || isValidating || Object.keys(errors).length > 0
            }
            shape="square"
            size="lg"
            color="secondary"
            type="submit"
            data-testid="submit"
          >
            Submit Registration
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default SenderIdConfigurationForm;
